import { Text } from "@/components/ui/baseUi/text"
import type { ProgramCalendarEventType } from "@/modules/calendar/calendarTypes"
import { cn } from "@/modules/lib/utils"
import { NoteForAthletesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForAthletesInEvent"
import { NoteForCoachesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForCoachesInEvent"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { MediaDetailsInEvent } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsInEvent"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { TemplateCalendarSessionBlockContainer } from "@/modules/template/components/calendar/templateCalendarSessionBlockContainer"
import { TemplateCalendarSessionFooterContainer } from "@/modules/template/components/calendar/templateCalendarSessionFooter"
import { TemplateCalendarSessionHeaderContainer } from "@/modules/template/components/calendar/templateCalendarSessionHeader"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { useSessionBlockSort } from "@modules/programs/hooks/useSessionBlockSort"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useInvalidateTemplate } from "@modules/template/hooks/useTemplate"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { LoaderCircle } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"

type TemplateWeekCalendarEventProps = {
  index: number
  templateId: string
  event: ProgramCalendarEventType
  session: TemplateSessionEntity
  displayDetailedEvents?: boolean
  onEventClick?: (session: TemplateSessionEntity) => void
  onBlockEditClick: (session: TemplateSessionEntity, orderIndex: number) => void
  onCreateBlockClick: (session: TemplateSessionEntity, newBlockType: SessionBlockType) => void
  isUpdating?: boolean
  isDetailsOpen?: boolean
  mode: "preview" | "edit"
} & React.ComponentProps<"div">

export function TemplateWeekCalendarEvent({
  displayDetailedEvents = false,
  templateId,
  event,
  session,
  onEventClick,
  onBlockEditClick,
  onCreateBlockClick,
  isUpdating = false,
  isDetailsOpen = false,
  mode,
  className,
  ...props
}: TemplateWeekCalendarEventProps) {
  const { setDraggingEvent, setIsDragging } = useCalendar()
  const programCalendarEventRef = useRef<HTMLDivElement>(null)
  const { isSidebarVisible } = useSidebar()
  const { moveBlockMutation } = useSessionBlockSort()
  const { invalidateQueryTemplate } = useInvalidateTemplate(templateId)
  const canSeeActions = useMemo(() => !isSidebarVisible && mode === "edit", [isSidebarVisible, mode])

  const visibleBlocks = useMemo(() => event.blocks, [event.blocks])
  const firstBlockIndex = visibleBlocks[0]?.content.orderIndex ?? 0
  const lastBlockIndex = visibleBlocks[visibleBlocks.length - 1]?.content.orderIndex ?? visibleBlocks.length - 1

  const onMoveBlock = (direction: "UP" | "DOWN", orderIndex: number) => {
    if (mode === "preview") return
    if (direction === "UP" && orderIndex === firstBlockIndex) return
    if (direction === "DOWN" && orderIndex === lastBlockIndex) return

    const currentBlockIndex = visibleBlocks.findIndex((block) => block.content.orderIndex === orderIndex)

    const newBlockIndex =
      direction === "UP"
        ? visibleBlocks[currentBlockIndex - 1]?.content.orderIndex
        : visibleBlocks[currentBlockIndex + 1]?.content.orderIndex

    if (newBlockIndex === undefined) return

    void moveBlockMutation({ sessionId: event.id, blockIndex: orderIndex, newBlockIndex }).then(() => {
      invalidateQueryTemplate()
    })
  }

  const handleSeeSessionClick = () => {
    if (!onEventClick || mode === "preview") return
    onEventClick(session)
  }

  const shouldDisplayTags = event.cardioTag || event.gymnasticTag || event.weightliftingTag

  useEffect(() => {
    const programCalendarEvent = programCalendarEventRef.current
    const dragstart = () => {
      setIsDragging(true)
      setDraggingEvent(event)
    }

    programCalendarEvent?.addEventListener("dragstart", dragstart)
    return () => {
      programCalendarEvent?.removeEventListener("dragstart", dragstart)
    }
  }, [event, setDraggingEvent, setIsDragging])

  return (
    <div key={event.id} id={event.id} ref={programCalendarEventRef} className="border-b pb-2">
      <div
        className={cn(
          "transition-in gap-2 rounded-3xl bg-linear-to-b from-primary-100 from-0% via-primary-100 via-[50px] to-[100px] p-0 pb-2 transition-colors duration-200 has-[#session-header:hover]:to-primary-100 has-[.session-content-stack:hover]:from-transparent has-[.session-content-stack:hover]:via-transparent has-[.session-content-stack:hover]:to-transparent",
          {
            "cursor-pointer": !!onEventClick,
            "to-primary-100": isDetailsOpen,
            "to-surface-background": !isDetailsOpen,
            "bg-neutral-100": "restDay" in event && event.restDay,
            "pointer-events-none": mode === "preview",
          },
          className,
          props,
        )}
        onClick={() => {
          handleSeeSessionClick()
        }}
      >
        <>
          <TemplateCalendarSessionHeaderContainer
            onSeeSessionClick={() => {
              handleSeeSessionClick()
            }}
            session={session}
            canSeeActions={canSeeActions}
            onCreateBlockClick={onCreateBlockClick}
          >
            <div className="flex flex-row items-center justify-between p-2">
              <Text fontStyle="TM" className="font-bold text-textDefault-body" data-event-title>
                {event.title}
              </Text>

              {isUpdating && <LoaderCircle className="h-4 w-4 animate-spin text-green-300" />}
            </div>

            {shouldDisplayTags && (
              <div className="flex flex-row items-center justify-start gap-2 px-2">
                {event.gymnasticTag && <GymSelectedIcon color="" width={20} height={20} />}
                {event.weightliftingTag && <WeightliftingSelectedIcon color="" width={20} height={20} />}
                {event.cardioTag && <CardioSelectedIcon color="" width={20} height={20} />}
              </div>
            )}
          </TemplateCalendarSessionHeaderContainer>

          {visibleBlocks.length > 0 && (
            <div className="session-content-stack relative mt-2 flex flex-col gap-2">
              {visibleBlocks.map((block, blockIndex) => (
                <TemplateCalendarSessionBlockContainer
                  key={blockIndex}
                  canSeeActions={canSeeActions}
                  orderIndex={block.content.orderIndex}
                  firstBlockIndex={firstBlockIndex}
                  lastBlockIndex={lastBlockIndex}
                  session={session}
                  onMoveBlock={onMoveBlock}
                  onBlockEditClick={onBlockEditClick}
                >
                  {block.type === SessionBlockType.SESSION_EXERCISE ? (
                    <div className="flex flex-col gap-2" key={blockIndex}>
                      <Text fontStyle="TS" className="font-sora text-base">
                        {getDisplayExerciseCategory(block.content.category, block.content.otherCategory)}
                      </Text>

                      {block.content.chrono?.type && (
                        <div className="flex items-center">
                          <ChronoIcon color="currentColor" className="text-textDefault-body" width={14} height={14} />
                          <div className="flex flex-col gap-0">
                            <Text fontStyle="LS" className="ml-2 text-textDefault-body">
                              {getTrainingChronoTitle(block.content.chrono.type)}
                            </Text>
                            <Text fontStyle="LS" className="ml-2 text-textDefault-body">
                              {displayTotalTime(block.content.chrono)}
                            </Text>
                          </div>
                        </div>
                      )}
                      <HURichTextView
                        value={formatTextToRichText(block.content.description)}
                        paragrapheFontStyle="BS"
                        className={cn(
                          displayDetailedEvents ? "" : "line-clamp-3 wrap-break-word whitespace-break-spaces",
                        )}
                      />
                    </div>
                  ) : block.type === SessionBlockType.COACH_NOTE ? (
                    <NoteForCoachesInEvent
                      key={blockIndex}
                      note={block.content}
                      displayDetailedEvents={displayDetailedEvents}
                    />
                  ) : block.type === SessionBlockType.ATHLETE_NOTE ? (
                    <NoteForAthletesInEvent
                      key={blockIndex}
                      note={block.content}
                      displayDetailedEvents={displayDetailedEvents}
                    />
                  ) : (
                    // block.type === SessionBlockType.MEDIAS :
                    <MediaDetailsInEvent
                      key={blockIndex}
                      medias={block.content.medias}
                      displayDetailedEvents={displayDetailedEvents}
                    />
                  )}
                </TemplateCalendarSessionBlockContainer>
              ))}
            </div>
          )}
          <TemplateCalendarSessionFooterContainer
            session={session}
            onCreateBlockClick={onCreateBlockClick}
            className={canSeeActions ? "" : "opacity-0"}
          />
        </>
      </div>
    </div>
  )
}
